/**
 * Dashboard data hooks (issue #16) — one per role, each a thin useQuery
 * wrapper over dashboardApi, pattern from use-tenant-config.ts.
 *
 * QUERY KEY (REQUIRED — AC gate "no stale cross-tenant cached counts on
 * tenant switch"): includes role + tenantSlugFromHostname() + me.user_id.
 * The user_id component is load-bearing on top of the tenant-only design: a
 * same-tenant sign-out-and-different-user-sign-in in the same tab must not
 * serve the first user's cached dashboard data to the second (React Query's
 * cache lives outside the auth lifecycle and is otherwise never invalidated
 * on identity change alone). Combined with AuthContext.signOut()'s cache
 * clear, this closes both the cross-tenant AND cross-user-same-tenant stale
 * cache scenarios.
 *
 * Per-hook overrides (staleTime/refetchInterval/refetchOnWindowFocus/retry)
 * are LOCAL to these hooks — the global QueryClientProvider defaults in
 * App.tsx are untouched and keep serving every other page.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { dashboardApi } from "@/lib/api/dashboard";
import { tenantSlugFromHostname } from "@/lib/tenant";

const DASHBOARD_QUERY_OPTIONS = {
  staleTime: 30_000,
  refetchInterval: 60_000,
  refetchOnWindowFocus: true,
  retry: 1,
} as const;

export function useAdminDashboard() {
  const { me } = useAuth();
  return useQuery({
    queryKey: ["dashboard", "admin", tenantSlugFromHostname(), me?.user_id ?? "anon"],
    queryFn: () => dashboardApi.admin(),
    ...DASHBOARD_QUERY_OPTIONS,
  });
}

export function useInstructorDashboard() {
  const { me } = useAuth();
  return useQuery({
    queryKey: ["dashboard", "instructor", tenantSlugFromHostname(), me?.user_id ?? "anon"],
    queryFn: () => dashboardApi.instructor(),
    ...DASHBOARD_QUERY_OPTIONS,
  });
}

export function useStudentDashboard() {
  const { me } = useAuth();
  return useQuery({
    queryKey: ["dashboard", "student", tenantSlugFromHostname(), me?.user_id ?? "anon"],
    queryFn: () => dashboardApi.student(),
    ...DASHBOARD_QUERY_OPTIONS,
  });
}
